<template>
  <q-dialog
    ref="dialog"
    :maximized="$q.platform.is.mobile"
    @hide="onDialogHide"
  >
    <q-card
      id="multiple-trials-dialog"
      class="q-pa-sm"
      data-e2e="dialog-add-multiple-trials"
    >
      <q-card-section class="row">
        <div class="text-h6 col ellipsis">
          {{ dialogTitle }}
        </div>
        <q-btn
          v-close-popup
          flat
          round
          dense
          icon="close"
        />
      </q-card-section>
      <q-form @submit="onSubmitForm">
        <q-card-section class="q-pt-none">
          <q-input
            v-if="!hasMultiplePromptLevels"
            v-model.number="trialsNumber"
            min="0"
            :label="$t('sessions.trialOverview.modals.addMultipleTrials.trialsInputLabel')"
            dense
            outlined
            type="number"
            required
            autofocus
            pattern="[0-9]*"
            data-e2e="input-trials-number"
            :rules="[
              val => (val >= minValue)
                || $t('sessions.trialOverview.modals.addMultipleTrials.trialsInputValidator')
            ]"
          />
          <div
            class="q-mb-sm"
          >
            <q-input
              v-if="showDuration || isDurationData"
              v-model="durationTime"
              dense
              outlined
              :label="$t('sessions.trialOverview.durationInputLabel')"
              mask="#:##:##"
              autofocus
            />
          </div>
          <q-list
            v-if="hasMultiplePromptLevels"
            style="min-width: 100px"
          >
            <q-item
              v-for="editPromptLevel in editTrialsPerPromptLevel"
              :key="editPromptLevel.promptLevel.id"
              clickable
              :style="getPromptLevelStyle(editPromptLevel.promptLevel)"
              class="prompt-level row items-center"
              @click="onAddData(editPromptLevel.promptLevel)"
            >
              <q-item-section>{{ editPromptLevel.promptLevel.trialOutcome }}</q-item-section>
              <q-item-section>
                <q-input
                  v-model.number="editPromptLevel.value"
                  min="0"
                  outlined
                  type="number"
                  dense
                  required
                  autofocus
                  pattern="[0-9]*"
                  hide-bottom-space
                  class="bg-white"
                />
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
        <q-card-actions>
          <q-btn
            v-if="canDelete"
            flat
            :label="$t('common.buttonDelete')"
            color="negative"
            @click="onDelete"
          />
          <q-space />
          <q-btn
            v-close-popup
            flat
            :label="$t('common.buttonCancel')"
            color="secondary"
          />
          <q-btn
            flat
            :label="$t('common.buttonSave')"
            type="submit"
            color="primary"
            :disable="isSaveDisabled"
            data-e2e="button-save"
          />
        </q-card-actions>
      </q-form>
    </q-card>
  </q-dialog>
</template>

<script>
import { chain, get, isArray, map, sumBy } from 'lodash';
import { mapState } from 'pinia';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { promptLevels } from 'src/store/promptLevels';

export default {
  name: 'AddMultipleTrials',

  mixins: [Targets, Toolbox],

  props: {
    program: {
      type: Object,
      required: true,
    },
    promptLevelTemplate: {
      type: Object,
      required: true,
    },
    title: {
      type: String,
      default: null,
    },
    data: {
      type: Object,
      default: () => ({}),
    },
    duration: {
      type: Number,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
    allowZero: {
      type: Boolean,
      default: false,
    },
    showDuration: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['ok', 'hide'],

  data() {
    return {
      editTrialsPerPromptLevel: {},

      durationTime: '0:00:00',
    };
  },

  computed: {
    ...mapState(promptLevels, ['activePromptLevels']),

    dialogTitle() {
      return this.title || this.$t('sessions.trialOverview.modals.addMultipleTrials.title');
    }, // title()

    isDurationData() {
      return get(this.program, ['data', 'dataType']) === 'Duration1.0';
    }, // isDurationData()

    programPrompts() {
      return get(this.promptLevelTemplate, 'promptLevels', []);
    }, // programPrompts()

    hasMultiplePromptLevels() {
      if (!isArray(this.programPrompts)) {
        return false;
      }

      return this.programPrompts.length !== 1;
    }, // hasMultiplePromptLevels()

    isSaveDisabled() {
      return !this.allowZero && (sumBy(this.editTrialsPerPromptLevel, 'value') === 0);
    }, // isSaveDisabled()

    minValue() {
      return this.allowZero ? 0 : 1;
    }, // minValue()

    trialsNumber: {
      get() {
        return chain(this.editTrialsPerPromptLevel).head().get('value').value();
      },
      set(value) {
        this.editTrialsPerPromptLevel = map(
          this.editTrialsPerPromptLevel,
          (promptLevelInfo) => ({ ...promptLevelInfo, value }),
        );
      },
    },

  },

  methods: {
    onSubmitForm() {
      this.$emit('ok', {
        data: this.editTrialsPerPromptLevel,
        duration: this.showDuration ? this.stringToDuration(this.durationTime) : null,
        delete: false,
      });
      this.hide();
    }, // onSubmitForm()

    onDelete() {
      this.$emit('ok', { data: null, duration: null, deleteTrial: true });
      this.hide();
    }, // onDelete()

    show() {
      this.$refs.dialog.show();
      this.initData();
    },

    hide() {
      if (this.$refs.dialog) {
        this.$refs.dialog.hide();
      }
    },

    onAddData(value) {
      this.selectedPromptLevel = value;
    },

    onDialogHide() {
      this.$emit('hide');
    },

    initData() {
      this.editTrialsPerPromptLevel = chain(this.promptLevelTemplate)
        .get('promptLevels', [])
        .map((promptLevel) => ({ promptLevel, value: get(this.data, promptLevel.id, 0) }))
        .value();

      if (this.showDuration) {
        this.durationTime = this.formatDuration(this.duration);
      }
    }, // initData()

  },
};
</script>

<style scoped>
#multiple-trials-dialog {
  width: 400px;
}
</style>
